import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { useFrameLoader } from '../../../hooks/useFrameLoader';
import { useLocalFramePreviewVideoUrl } from '../../../hooks/useLocalPreviewVideoUrl';
import { extractLocalFrameThumbnails } from '../../../services/LocalFrameThumbnails';
import { useVideoCreatorStore } from '../../../stores-v2/VideoCreatorStoreContext';
import { isCompressedRenditionPending } from '../../../utility/storyReadiness';
import {
  addCacheUpdateListener,
  isImageAlreadyLoaded,
  markImageAsLoaded,
} from '../../../utility/thumbnails';
import {
  addFrameCacheUpdateListener,
  findNearestCachedFrames,
  getCachedFrameUrls,
  setCachedFrameUrls,
} from '../../../utility/videoFrameCache';
import SimpleLoadingSpinner from '../../SimpleLoadingSpinner';

import type { Story } from '../../../types.ts/story';

interface TimelineFrameThumbnailsProps {
  storyId: string;
  videoId: string;
  elementId: string;
  trimStart: number; // Start time in seconds
  duration: number; // Duration in seconds
  elementWidth: number; // Width in pixels on timeline
  height: number; // Thumbnail height
  videoWidth?: number; // Original video width
  videoHeight?: number; // Original video height
  fallbackThumbnailUrl?: string; // URL for fallback thumbnail
  hasThumbnails?: boolean; // Track if video has thumbnails available
}

const Container = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const FrameImage = styled.img<{ isLoading: boolean }>`
  height: 100%;
  object-fit: cover;
  opacity: ${props => (props.isLoading ? 0.5 : 1)};
  transition: opacity var(--transition-normal) ease-in-out;
  will-change: opacity;
`;

const PlaceholderFrame = styled.div`
  background-color: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const LoadingMessage = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: normal;
`;

// Frame calculation constants
const TARGET_FRAME_WIDTH = 60;
const MIN_FRAME_WIDTH = 30;
const MAX_FRAME_WIDTH = 120;
const ABSOLUTE_MAX_FRAMES = 300;
const SERVER_FRAME_RETRY_MS = 5000;

// Track videos that don't have frame files (404 errors)
const videosWithoutFrames = new Set<string>();

// Track videos with images that are still processing (403 errors)
const videosWithProcessingImages = new Set<string>();

interface FrameData {
  index: number;
  frameNumber: number;
  time: number;
  width: number;
  url?: string; // Populated from cache or API
  isLoaded?: boolean; // Track if image has loaded successfully
}

// Helper to get cache key for a specific video
const getVideoCacheKey = (storyId: string, videoId: string) =>
  `${storyId}:${videoId}`;

// Helper function to find the closest frame from previous state
const findClosestPreviousFrame = (
  targetTime: number,
  prevFrames: FrameData[],
): FrameData | undefined => {
  if (prevFrames.length === 0) return undefined;

  let closestFrame: FrameData | undefined;
  let minTimeDiff = Infinity;

  for (const frame of prevFrames) {
    if (frame.url && frame.isLoaded) {
      const timeDiff = Math.abs(frame.time - targetTime);
      if (timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        closestFrame = frame;
      }
    }
  }

  return closestFrame;
};

// Helper function to create frame with preserved state
const createFrameWithCache = (
  newFrame: FrameData,
  existingFrame: FrameData | undefined,
  cachedUrl: string | undefined,
  closestPreviousFrame: FrameData | undefined,
): FrameData => {
  // If we have an existing frame with the same frame number, preserve its loading state
  if (existingFrame && existingFrame.url) {
    return {
      ...newFrame,
      url: existingFrame.url,
      isLoaded: existingFrame.isLoaded || false,
    };
  }

  // If we have a cached URL, use it but mark as not loaded yet (needs image load)
  if (cachedUrl) {
    return {
      ...newFrame,
      url: cachedUrl,
      isLoaded: isImageAlreadyLoaded(cachedUrl),
    };
  }

  // If we have a closest previous frame, use it as dimmed placeholder
  if (closestPreviousFrame && closestPreviousFrame.url) {
    return {
      ...newFrame,
      url: closestPreviousFrame.url,
      isLoaded: false, // Mark as not loaded so it shows dimmed
    };
  }

  // No cached URL available
  return {
    ...newFrame,
    url: undefined,
    isLoaded: false,
  };
};

// Helper to determine thumbnail loading state based on story data
const getThumbnailLoadingState = (
  story: Story | undefined,
  videoCacheKey: string,
  hasAttemptedLoad: boolean,
  isCurrentlyLoading: boolean,
): ThumbnailLoadingState => {
  // Scenario 1: Pre-AWS video (no thumbnails available)
  if (story && !story.useAws) {
    return {
      state: 'unavailable',
      message: 'Thumbnails not available for this video',
    };
  }

  // Scenario 2: Previously failed to load (404/500 errors)
  if (videosWithoutFrames.has(videoCacheKey)) {
    return {
      state: 'failed',
      message: 'Unable to load thumbnails',
    };
  }

  // Scenario 3: Images are still being processed (403 errors from CloudFront)
  if (videosWithProcessingImages.has(videoCacheKey)) {
    return {
      state: 'processing',
      message:
        "We're still loading your thumbnails... but feel free to get started.",
    };
  }

  // Scenario 4: AWS video that's currently loading for the first time
  if (story?.useAws && isCurrentlyLoading && !hasAttemptedLoad) {
    return {
      state: 'processing',
      message:
        "We're still loading your thumbnails... but feel free to get started.",
    };
  }

  return { state: 'available' };
};

// Thumbnail loading states
type ThumbnailLoadingState =
  | { state: 'available' }
  | { state: 'processing'; message: string }
  | { state: 'unavailable'; message: string }
  | { state: 'failed'; message: string };

export const TimelineFrameThumbnails: React.FC<TimelineFrameThumbnailsProps> =
  observer(
    ({
      storyId,
      videoId,
      elementId,
      trimStart,
      duration,
      elementWidth,
      height,
      videoWidth,
      videoHeight,
      fallbackThumbnailUrl,
      hasThumbnails,
    }) => {
      const videoCreator = useVideoCreatorStore();
      const videoCacheKey = getVideoCacheKey(storyId, videoId);
      // PLAT-11228: the session may still hold the uploaded file — frames
      // can be cut from it locally while the pipeline's are still processing.
      const localPreviewUrl = useLocalFramePreviewVideoUrl(storyId);
      const localExtractInFlightRef = useRef(false);
      const renditionPending =
        !!videoCreator.story &&
        isCompressedRenditionPending(videoCreator.story);
      const [frames, setFrames] = useState<FrameData[]>([]);
      const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
      const [shouldUseFallback, setShouldUseFallback] = useState(
        videosWithoutFrames.has(videoCacheKey),
      );

      // Generate unique element ID for tracking loading state
      const elementIdRef = useRef(
        `thumbnail-${storyId}-${videoId}-${elementId}-${Math.random()
          .toString(36)
          .substring(2, 11)}`,
      );
      const trackingElementId = elementIdRef.current;

      // Calculate which frames to display (using proven logic from first implementation)
      const frameData = useMemo(() => {
        if (!videoCreator.isVideoCreatorReady) {
          return [];
        }

        // Calculate optimal frame width based on aspect ratio
        let optimalFrameWidth: number;
        if (videoWidth && videoHeight && videoHeight > 0) {
          const aspectRatio = videoWidth / videoHeight;
          optimalFrameWidth = Math.round(height * aspectRatio);
        } else {
          optimalFrameWidth = TARGET_FRAME_WIDTH;
        }

        // Clamp frame width to reasonable bounds
        const frameWidth = Math.max(
          MIN_FRAME_WIDTH,
          Math.min(MAX_FRAME_WIDTH, optimalFrameWidth),
        );

        // Calculate frame count based on available space
        let frameCount = Math.max(1, Math.floor(elementWidth / frameWidth));

        // Limit frame count to available duration
        const maxAvailableFrames = Math.max(1, Math.floor(duration));

        // Apply performance limits
        frameCount = Math.min(
          frameCount,
          maxAvailableFrames,
          ABSOLUTE_MAX_FRAMES,
        );

        // Calculate frame interval
        const frameInterval = duration / frameCount;

        // Generate frame data
        return Array.from({ length: frameCount }, (_, i) => {
          const time = trimStart + i * frameInterval + frameInterval / 2;
          let frameNumber: number;

          if (frameInterval < 1) {
            frameNumber = Math.max(
              1,
              Math.round(trimStart + (i / (frameCount - 1)) * duration),
            );
          } else {
            frameNumber = Math.max(1, Math.round(time));
          }

          return {
            index: i,
            frameNumber,
            time,
            width: elementWidth / frameCount,
          };
        });
      }, [
        videoCreator.isVideoCreatorReady,
        trimStart,
        duration,
        elementWidth,
        height,
        videoWidth,
        videoHeight,
      ]);

      // Populate frames from cache immediately when frameData changes
      const populateFramesFromCache = useCallback(() => {
        if (frameData.length === 0) {
          setFrames([]);
          return;
        }

        // Get exact cached frames
        const exactCachedFrames = getCachedFrameUrls(
          storyId,
          videoId,
          frameData.map(f => f.frameNumber),
        );

        // Get nearby cached frames as fallback
        const nearestFrames = findNearestCachedFrames(
          storyId,
          videoId,
          frameData.map(f => f.frameNumber),
          2, // Allow frames within 2 seconds
        );

        // Update frames to match frameData, preserving existing states when possible
        setFrames(prevFrames => {
          const newFrames = frameData.map(newFrame => {
            // Try to find existing frame with same frame number to preserve state
            const existingFrame = prevFrames.find(
              f => f.frameNumber === newFrame.frameNumber,
            );

            // First try exact match
            let cachedUrl = exactCachedFrames.get(newFrame.frameNumber);

            // If no exact match, try nearest frame
            if (!cachedUrl) {
              const nearestFrame = nearestFrames.get(newFrame.frameNumber);
              cachedUrl = nearestFrame?.url;
            }

            // Find closest previous frame if no exact match and no cached URL
            let closestPreviousFrame: FrameData | undefined;
            if (!existingFrame && !cachedUrl) {
              closestPreviousFrame = findClosestPreviousFrame(
                newFrame.time,
                prevFrames,
              );
            }

            return createFrameWithCache(
              newFrame,
              existingFrame,
              cachedUrl,
              closestPreviousFrame,
            );
          });

          return newFrames;
        });
      }, [frameData, storyId, videoId]);

      // Populate frames from cache when frameData changes
      useEffect(() => {
        populateFramesFromCache();
      }, [populateFramesFromCache]);

      // Handle frames loaded from worker
      const handleFramesLoaded = useCallback(
        (incomingFrames: Array<{ frameNumber: number; url: string }>) => {
          // Pipeline frames replace session-local blobs as soon as they are
          // fetchable. A late local seek may not overwrite a server frame.
          const loadedFrames = incomingFrames.filter(frame => {
            const previous = getCachedFrameUrls(storyId, videoId, [
              frame.frameNumber,
            ]).get(frame.frameNumber);
            return !(
              frame.url.startsWith('blob:') &&
              previous &&
              !previous.startsWith('blob:')
            );
          });
          if (loadedFrames.length === 0) return;
          // Cache the newly loaded frames
          setCachedFrameUrls(storyId, videoId, loadedFrames);

          // Clear processing state since we successfully loaded frames
          videosWithProcessingImages.delete(videoCacheKey);

          // Update frames with newly loaded URLs, replacing dimmed placeholders
          setFrames(prevFrames => {
            const updatedFrames = prevFrames.map(frame => {
              const loadedFrame = loadedFrames.find(
                lf => lf.frameNumber === frame.frameNumber,
              );
              if (loadedFrame) {
                return {
                  ...frame,
                  url: loadedFrame.url,
                  isLoaded: false, // Will be set to true when image loads
                };
              }
              return frame;
            });

            return updatedFrames;
          });
        },
        [storyId, videoId, videoCacheKey],
      );

      // Handle load errors - fallback to legacy behavior
      const handleLoadError = useCallback(() => {
        videosWithoutFrames.add(videoCacheKey);
        setShouldUseFallback(true);
      }, [videoCacheKey]);

      // Handle images that are still processing (403 errors)
      const handleImagesProcessing = useCallback(() => {
        const frameNumbers = frameData.map(frame => frame.frameNumber);
        const cached = getCachedFrameUrls(storyId, videoId, frameNumbers);
        const hasLocalFrames = Array.from(cached.values()).some(url =>
          url.startsWith('blob:'),
        );
        if (hasLocalFrames) {
          videosWithProcessingImages.delete(videoCacheKey);
        } else {
          videosWithProcessingImages.add(videoCacheKey);
        }
      }, [frameData, storyId, videoId, videoCacheKey]);

      // Use the frame loader hook
      const { loadFrames, isLoading: isFrameLoading } = useFrameLoader({
        apiUrl: process.env.REACT_APP_API_URL || '',
        onFramesLoaded: handleFramesLoaded,
        onImagesProcessing: handleImagesProcessing,
      });
      const frameLoadingRef = useRef(isFrameLoading);
      useEffect(() => {
        frameLoadingRef.current = isFrameLoading;
      }, [isFrameLoading]);
      const serverProbeInFlightRef = useRef(false);

      // Check thumbnail loading state based on story data
      const thumbnailLoadingState = getThumbnailLoadingState(
        videoCreator.story,
        videoCacheKey,
        hasAttemptedLoad,
        isFrameLoading,
      );

      // PLAT-11228: while renditions are pending, the pipeline's per-second
      // thumbnails cannot exist yet (they are cut from the 270p at the very
      // end). If this session holds the uploaded file, cut the visible frames
      // from it locally and feed them through the normal loaded-frames path —
      // the filmstrip fills in seconds instead of at the end. Strictly
      // additive: no file, undecodable file, or any failure leaves the
      // existing behaviour exactly as it was.
      useEffect(() => {
        if (
          shouldUseFallback ||
          !renditionPending ||
          !localPreviewUrl ||
          frameData.length === 0 ||
          localExtractInFlightRef.current
        ) {
          return;
        }
        const frameNumbers = [...new Set(frameData.map(f => f.frameNumber))];
        const cached = getCachedFrameUrls(storyId, videoId, frameNumbers);
        const needed = frameNumbers.filter(
          num => !(cached.get(num) ?? '').startsWith('blob:'),
        );
        if (needed.length === 0) return;

        const controller = new AbortController();
        localExtractInFlightRef.current = true;
        void extractLocalFrameThumbnails(
          localPreviewUrl,
          needed,
          height,
          controller.signal,
        ).then(localFrames => {
          localExtractInFlightRef.current = false;
          if (controller.signal.aborted || localFrames.length === 0) return;
          handleFramesLoaded(localFrames);
        });
        return () => {
          controller.abort();
          localExtractInFlightRef.current = false;
        };
      }, [
        renditionPending,
        shouldUseFallback,
        localPreviewUrl,
        frameData,
        height,
        storyId,
        videoId,
        handleFramesLoaded,
      ]);

      // Compressed-video readiness can flip before per-second JPEG extraction
      // finishes. Keep the local filmstrip visible, but probe every five
      // seconds and atomically replace its blobs with pipeline URLs on the
      // first successful response.
      useEffect(() => {
        if (
          shouldUseFallback ||
          renditionPending ||
          !hasThumbnails ||
          frameData.length === 0
        ) {
          return;
        }

        const requestServerFrames = () => {
          if (frameLoadingRef.current || serverProbeInFlightRef.current) {
            return false;
          }
          const frameNumbers = [...new Set(frameData.map(f => f.frameNumber))];
          const cached = getCachedFrameUrls(storyId, videoId, frameNumbers);
          const pending = frameNumbers.filter(frameNumber => {
            const url = cached.get(frameNumber);
            return !url || url.startsWith('blob:');
          });
          if (pending.length === 0) return true;
          setHasAttemptedLoad(true);
          serverProbeInFlightRef.current = true;
          loadFrames(storyId, videoId, pending, handleLoadError, () => {
            serverProbeInFlightRef.current = false;
          });
          return false;
        };

        const timer = setInterval(() => {
          if (requestServerFrames()) clearInterval(timer);
        }, SERVER_FRAME_RETRY_MS);
        return () => clearInterval(timer);
      }, [
        renditionPending,
        shouldUseFallback,
        hasThumbnails,
        frameData,
        storyId,
        videoId,
        loadFrames,
        handleLoadError,
      ]);

      // Handle fallback when frame count exceeds threshold
      useEffect(() => {
        if (frameData.length === 0 && !shouldUseFallback) {
          setShouldUseFallback(true);
        } else if (
          frameData.length > 0 &&
          shouldUseFallback &&
          !videosWithoutFrames.has(videoCacheKey)
        ) {
          // Reset fallback if we now have frameData and haven't previously failed
          setShouldUseFallback(false);
        }
      }, [frameData.length, shouldUseFallback, videoCacheKey]);

      // Load frames when needed
      useEffect(() => {
        // Skip if already fell back to legacy, or thumbnails not available
        if (
          shouldUseFallback ||
          thumbnailLoadingState.state !== 'available' ||
          !hasThumbnails
        ) {
          return;
        }

        // Get frame numbers that need to be loaded
        const frameNumbers = frameData.map(f => f.frameNumber);

        // Check which frames are already cached (exact + nearby)
        const exactCachedFrames = getCachedFrameUrls(
          storyId,
          videoId,
          frameNumbers,
        );
        const nearestFrames = findNearestCachedFrames(
          storyId,
          videoId,
          frameNumbers,
          2,
        );

        // Only load frames that aren't cached and don't have nearby alternatives
        const uncachedFrames = frameNumbers.filter(
          num => !exactCachedFrames.has(num) && !nearestFrames.has(num),
        );

        if (uncachedFrames.length > 0) {
          setHasAttemptedLoad(true);
          loadFrames(storyId, videoId, uncachedFrames, handleLoadError);
        }
      }, [
        storyId,
        videoId,
        frameData,
        loadFrames,
        shouldUseFallback,
        handleLoadError,
        thumbnailLoadingState.state,
        hasThumbnails,
      ]);

      // Track loading state with VideoCreatorStore
      useEffect(() => {
        if (shouldUseFallback || thumbnailLoadingState.state !== 'available') {
          videoCreator.unregisterThumbnailLoading(trackingElementId);
          return;
        }

        if (isFrameLoading && !videoCreator.isVideoCreatorReady) {
          videoCreator.registerThumbnailLoading(trackingElementId);
        } else {
          videoCreator.unregisterThumbnailLoading(trackingElementId);
        }

        // Cleanup on unmount
        return () => {
          videoCreator.unregisterThumbnailLoading(trackingElementId);
        };
      }, [
        isFrameLoading,
        trackingElementId,
        videoCreator,
        shouldUseFallback,
        thumbnailLoadingState.state,
      ]);

      // Listen for cache updates from other components
      useEffect(() => {
        const unsubscribeFrameCache = addFrameCacheUpdateListener(() => {
          // Check if any frames without URLs now have cached URLs available
          setFrames(prevFrames => {
            let hasUpdates = false;
            const updatedFrames = prevFrames.map(frame => {
              // If frame doesn't have URL but is now cached, update it
              if (!frame.url) {
                const cachedFrames = getCachedFrameUrls(storyId, videoId, [
                  frame.frameNumber,
                ]);
                const cachedUrl = cachedFrames.get(frame.frameNumber);
                if (cachedUrl) {
                  hasUpdates = true;
                  return {
                    ...frame,
                    url: cachedUrl,
                    isLoaded: isImageAlreadyLoaded(cachedUrl),
                  };
                }
              }
              return frame;
            });
            return hasUpdates ? updatedFrames : prevFrames;
          });
        });

        const unsubscribeImageCache = addCacheUpdateListener(() => {
          setFrames(prevFrames => {
            let hasUpdates = false;
            const updatedFrames = prevFrames.map(frame => {
              if (
                frame.url &&
                !frame.isLoaded &&
                isImageAlreadyLoaded(frame.url)
              ) {
                hasUpdates = true;
                return { ...frame, isLoaded: true };
              }
              return frame;
            });
            return hasUpdates ? updatedFrames : prevFrames;
          });
        });

        return () => {
          unsubscribeFrameCache();
          unsubscribeImageCache();
        };
      }, [storyId, videoId]);

      // Handle individual image loading
      const handleImageLoad = useCallback(
        (frameIndex: number, imageSrc: string) => {
          markImageAsLoaded(imageSrc);
          setFrames(prevFrames =>
            prevFrames.map(frame =>
              frame.index === frameIndex ? { ...frame, isLoaded: true } : frame,
            ),
          );
        },
        [],
      );

      // Render legacy single thumbnail if fallback is needed
      if (shouldUseFallback && fallbackThumbnailUrl) {
        return (
          <Container
            style={{
              height,
              backgroundImage: `url("${fallbackThumbnailUrl}")`,
              backgroundRepeat: 'repeat-x',
              backgroundSize: 'auto 100%',
            }}
          />
        );
      }

      return (
        <Container style={{ height }}>
          {thumbnailLoadingState.state === 'unavailable' ||
          thumbnailLoadingState.state === 'failed' ? (
            // Show fallback thumbnail for unavailable or failed states
            fallbackThumbnailUrl ? (
              <Container
                style={{
                  height,
                  backgroundImage: `url("${fallbackThumbnailUrl}")`,
                  backgroundRepeat: 'repeat-x',
                  backgroundSize: 'auto 100%',
                }}
              />
            ) : (
              <>
                <PlaceholderFrame style={{ width: '100%', height }} />
                <LoadingOverlay>
                  <LoadingMessage>
                    {thumbnailLoadingState.message}
                  </LoadingMessage>
                </LoadingOverlay>
              </>
            )
          ) : thumbnailLoadingState.state === 'processing' ? (
            // Show loading overlay for processing thumbnails
            <>
              <PlaceholderFrame style={{ width: '100%', height }} />
              <LoadingOverlay>
                <SimpleLoadingSpinner
                  spinnerSize={24}
                  spinnerWidth={3}
                  icon={null}
                  text={null}
                />
                <LoadingMessage>{thumbnailLoadingState.message}</LoadingMessage>
              </LoadingOverlay>
            </>
          ) : (
            frames.map(frame => {
              if (frame.url) {
                return (
                  <FrameImage
                    key={`${frame.index}-${frame.frameNumber}`}
                    src={frame.url}
                    style={{ width: frame.width }}
                    isLoading={!frame.isLoaded}
                    alt={`Frame at ${frame.time.toFixed(1)}s`}
                    loading="lazy"
                    decoding="async"
                    onLoad={() =>
                      frame.url && handleImageLoad(frame.index, frame.url)
                    }
                  />
                );
              }

              // Show placeholder while loading
              return (
                <PlaceholderFrame
                  key={`${frame.index}-${frame.frameNumber}`}
                  style={{ width: frame.width, height }}
                />
              );
            })
          )}
        </Container>
      );
    },
  );

TimelineFrameThumbnails.displayName = 'TimelineFrameThumbnails';

export default TimelineFrameThumbnails;
